import * as React from "react";
import * as ReactDOM from "react-dom";
import { Store, Dispatch, bindActionCreators } from "redux";
import { Provider, connect } from "react-redux";
import { Action, actionCreatorFactory, isType } from "diy/redux/typedActions";
const actionCreator = actionCreatorFactory('diy/Overlays/');
import {objectOverride} from 'diy/redux/helper';
import StoreProvider from "diy/redux/StoreProvider";
import Modul, {ModuleType} from 'diy/module/Modul';

import ModuleOverlay, {
    ModuleOverlayStateProps, ModuleOverlayDispatchProps
} from "./components/ModuleOverlay";
import {ModuleLoadingMasks} from './components/ModuleLoadingMasks';

import {OverlayStatus} from "./components/Overlay";

export {OverlayStatus};

// OverlayStatusMap to be used from JS, where Typescript enums are unavailable
export const OverlayStatusMap = {
    HOVER: OverlayStatus.HOVER,
    ACTIVE: OverlayStatus.ACTIVE,
};

// =====
// STATE
// =====
interface AppState {
    overlays: OverlaysState;
}
interface ModuleOverlayState {
    status: OverlayStatus;
    dragging: boolean;
    moduleId: number;
    menuOpen: boolean;
    renderSpotlight: boolean;
}
type LoadingModuleIdsState = number[];
interface OverlaysState {
    /* State for top-level module overlays */
    moduleOverlay: ModuleOverlayState;
    /* State for nested module overlays (inside sections) */
    moduleOverlayDeep: ModuleOverlayState;
    loadingModuleIds: LoadingModuleIdsState;
}
const initialState: OverlaysState = {
    moduleOverlay: null,
    moduleOverlayDeep: null,
    loadingModuleIds: [] as LoadingModuleIdsState
};

// ===============
// ACTION CREATORS
// ===============

type ModuleOverlayShowPayload = {
    moduleId: number;
    status?: OverlayStatus;
};
export const moduleOverlayShow = actionCreator<ModuleOverlayShowPayload>('MODULE_OVERLAY_SHOW');

type ModuleOverlayChangeStatusPayload = {
    status: OverlayStatus;
};
export const moduleOverlayChangeStatus =
    actionCreator<ModuleOverlayChangeStatusPayload>('MODULE_OVERLAY_CHANGE_STATUS');

type ModuleOverlayHidePayload = {
    moduleId: number;
};
export const moduleOverlayHide = actionCreator<ModuleOverlayHidePayload>('MODULE_OVERLAY_HIDE');

type ModuleLoadingPayload = {
    moduleId: number;
};
export const moduleLoadingStarted = actionCreator<ModuleLoadingPayload>('MODULE_LOADING_STARTED');
export const moduleLoadingFinished = actionCreator<ModuleLoadingPayload>('MODULE_LOADING_FINISHED');

type ModuleOverlayMenuTogglePayload = {
    open: boolean;
};
export const moduleOverlayMenuToggle = actionCreator<ModuleOverlayMenuTogglePayload>('MODULE_OVERLAY_MENU_TOGGLE');

export const moduleOverlayDragStarted = actionCreator<void>('MODULE_OVERLAY_DRAG_STARTED');

export const moduleOverlayDragStopped = actionCreator<void>('MODULE_OVERLAY_DRAG_STOPPED');

export function moduleOverlayClickOutside() {
    return (dispatch: Dispatch, getState: () => AppState) => {
        Modul.getInstance(getState().overlays.moduleOverlay.moduleId).onClickOutside();
    };
}

// =======
// REDUCER
// =======
function moduleOverlayReducer(
    state: ModuleOverlayState = initialState.moduleOverlay,
    action: Action<any>
): ModuleOverlayState {
    // don't handle other actions if it's in a dragging state
    if (state === null || !state.dragging) {
        if (isType(action, moduleOverlayShow)) {
            if (state === null ||
                (state.status !== OverlayStatus.ACTIVE)
            ) {
                return {
                    status: action.payload.status,
                    moduleId: action.payload.moduleId,
                    menuOpen: false,
                    dragging: false,
                    renderSpotlight: true
                };
            }
        }
        if (isType(action, moduleOverlayChangeStatus)) {
            let newState = Object.assign({}, state);
            newState.status = action.payload.status;
            return newState;
        }
        if (isType(action, moduleOverlayHide)) {
            if (state && action.payload.moduleId === state.moduleId) {
                return initialState.moduleOverlay;
            }
        }
        if (isType(action, moduleOverlayMenuToggle)) {
            let newState = Object.assign({}, state);
            newState.menuOpen = action.payload.open;
            return newState;
        }
    }
    if (isType(action, moduleOverlayDragStarted)) {
        let newState = Object.assign({}, state);
        newState.dragging = true;
        return newState;
    }
    if (isType(action, moduleOverlayDragStopped)) {
        return initialState.moduleOverlay;
    }
    return state;
}
function loadingModulesReducer(
    state: LoadingModuleIdsState = initialState.loadingModuleIds,
    action: Action<any>
) {
    if (isType(action, moduleLoadingStarted)) {
        if (state.indexOf(action.payload.moduleId) === -1) {
            return [...state, action.payload.moduleId];
        }
    }
    if (isType(action, moduleLoadingFinished)) {
        let index = state.indexOf(action.payload.moduleId);
        if (index !== -1) {
            return [...state.slice(0, index), ...state.slice(index + 1)];
        }
    }
    return state;
}
function reducer(
    state: OverlaysState = initialState,
    action: Action<any>
) {
    let moduleId = state.moduleOverlay ? state.moduleOverlay.moduleId : null;
    if (moduleId) {
        let moduleType = Modul.getInstance(moduleId).getModuleType();
        // If we have a section module in active state, we freeze the top level overlays
        // until we get a hide action on the section itself and route all actions
        // to the nested overlays reducer
        if (
            moduleType === ModuleType.SECTION &&
            state.moduleOverlay.status === OverlayStatus.ACTIVE
        ) {
            if (isType(action, moduleOverlayShow) && action.payload.moduleId === moduleId) {
                return state;
            }

            if (
                !(isType(action, moduleOverlayHide) && action.payload.moduleId === moduleId)
            ) {
                return objectOverride(state, {
                    moduleOverlay: state.moduleOverlay,
                    moduleOverlayDeep: moduleOverlayReducer(state.moduleOverlayDeep, action),
                    loadingModuleIds: loadingModulesReducer(state.loadingModuleIds, action)
                });
            }
        }
    }

    return objectOverride(state, {
        moduleOverlay: moduleOverlayReducer(state.moduleOverlay, action),
        moduleOverlayDeep: initialState.moduleOverlayDeep,
        loadingModuleIds: loadingModulesReducer(state.loadingModuleIds, action)
    });
}

// ======================
// CONNECT REACT TO REDUX
// ======================
function getMapStateToProps(selector: (state: AppState) => ModuleOverlayState) {
    return function mapStateToProps(state: AppState): ModuleOverlayStateProps {
        const ownState = selector(state);
        if (ownState === null) {
            return {
                status: null,
                moduleId: null,
                menuOpen: null,
                dragging: null
            };
        }

        return {
            status: ownState.status,
            moduleId: ownState.moduleId,
            menuOpen: ownState.menuOpen,
            dragging: ownState.dragging
        };
    };
};

function mapDispatchToProps(dispatch: Dispatch): ModuleOverlayDispatchProps {
    return bindActionCreators({
        onHideRequested: moduleOverlayHide,
        onMenuToggleRequested: moduleOverlayMenuToggle,
        onDragStarted: moduleOverlayDragStarted,
        onDragStopped: moduleOverlayDragStopped,
        onClickOutside: moduleOverlayClickOutside
    }, dispatch);
}

function render(store: Store) {
    let overlaysContainer = document.createElement('div');
    overlaysContainer.setAttribute('id', 'diy-overlays');
    document.body.appendChild(overlaysContainer);

    const ConnectedModuleOverlay = connect(
        getMapStateToProps(state => state.overlays.moduleOverlay),
        mapDispatchToProps
    )(ModuleOverlay);

    ReactDOM.render(
        <Provider store={store}>
            <ConnectedModuleOverlay/>
        </Provider>,
        overlaysContainer
    );
}

function renderDeep(store: Store) {
    let overlaysContainer = document.createElement('div');
    overlaysContainer.setAttribute('id', 'diy-overlays-deep');
    document.body.appendChild(overlaysContainer);

    const ConnectedModuleOverlayDeep = connect(
        getMapStateToProps(state => state.overlays.moduleOverlayDeep),
        mapDispatchToProps
    )(ModuleOverlay);

    ReactDOM.render(
        <Provider store={store}>
            <ConnectedModuleOverlayDeep/>
        </Provider>,
        overlaysContainer
    );
}

function renderLoadingMasks(store: Store) {
    let loadingMasksContainer = document.createElement('div');
    loadingMasksContainer.setAttribute('id', 'diy-module-loading-masks');
    document.body.appendChild(loadingMasksContainer);

    const ConnectedModuleLoadingMasks = connect(
        state => ({moduleIds: state.overlays.loadingModuleIds})
    )(ModuleLoadingMasks);

    ReactDOM.render(
        <Provider store={store}>
            <ConnectedModuleLoadingMasks/>
        </Provider>,
        loadingMasksContainer
    );
}

// ==============
// INITIALIZATION
// ==============
let initialized = false;
export function init(storeProvider: StoreProvider) {
    if (!initialized) {
        storeProvider.addReducer(reducer, "overlays");
        render(storeProvider.getStore());
        renderDeep(storeProvider.getStore());
        renderLoadingMasks(storeProvider.getStore());
        initialized = true;
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/overlay/index.tsx
 **/